import { integrationDraft, integrationValidation, publicIntegration, type AppBossIntegration, type AppBossIntegrationDraft } from './appBossIntegration';
import { publicIntegrationProbe, type IntegrationProbe } from './integrationProbe';

interface EnableConnectionIO {
  save: (draft: AppBossIntegrationDraft, existing: AppBossIntegration, rotate: boolean) => Promise<{ integration: AppBossIntegration; managementCredential?: string }>;
  test: (id: string, revision: number, signal?: AbortSignal) => Promise<IntegrationProbe>;
  onEnabled: (integration: AppBossIntegration) => void;
}

export async function enableAndTestConnection(
  selected: AppBossIntegration,
  draft: AppBossIntegrationDraft,
  confirmed: boolean,
  lock: { current: boolean },
  isCurrent: () => boolean,
  io: EnableConnectionIO,
  signal?: AbortSignal
): Promise<IntegrationProbe | null> {
  const current = () => isCurrent() && !signal?.aborted;
  if (!confirmed || lock.current || !current() || selected.status !== 'disabled' ||
      !Number.isSafeInteger(selected.revision) || selected.revision < 1 || selected.revision >= Number.MAX_SAFE_INTEGER ||
      integrationValidation(draft, selected) || JSON.stringify(draft) !== JSON.stringify(integrationDraft(selected))) return null;
  const source = publicIntegration(selected);
  const enabledDraft = { ...integrationDraft(source), status: 'enabled' as const };
  lock.current = true;
  try {
    const saved = await io.save(enabledDraft, source, false);
    if (!current()) return null;
    const next = saved.integration;
    if (!next || next.id !== source.id || next.status !== 'enabled' || !Number.isSafeInteger(next.revision) ||
        next.revision <= source.revision || next.revision >= Number.MAX_SAFE_INTEGER || saved.managementCredential ||
        integrationValidation(integrationDraft(next), next)) throw new Error('Invalid enabled connection response');
    const enabled = publicIntegration(next);
    io.onEnabled(enabled);
    if (!current()) return null;
    const result = await io.test(enabled.id, enabled.revision, signal);
    if (!current()) return null;
    return publicIntegrationProbe(result, enabled.id, enabled.revision);
  } finally {
    lock.current = false;
  }
}
